import React, {useState, useEffect, useContext, useMemo} from "react";
import {Auth0Client, Auth0ClientOptions, User} from "@auth0/auth0-spa-js";

export interface CallbackData {
  appState: any;
  user: any;
  token: string;
}

export type LoggedInCallback = (opts: CallbackData) => void;

// When Auth0 refuses a login, for example for an account without access to
// this app, it redirects back with ?error= instead of ?code=. The Auth0 session
// stays active, so logging in again only returns the same error.
function getCallbackError(): string | undefined {
  const params = new URLSearchParams(window.location.search);
  const error = params.get("error");
  if (!error) {
    return undefined;
  }
  return params.get("error_description") || error;
}

const DEFAULT_REDIRECT_CALLBACK = async (
  opts: CallbackData,
  next?: LoggedInCallback
) => {
  const {appState} = opts;

  // Redirect to app state target or current path, removing auth0 query params.
  // We use window.location.replace() because auth redirects are full page loads -
  // the router context isn't available yet when this runs after Auth0 redirect.
  const url =
    appState && appState.targetUrl
      ? appState.targetUrl
      : window.location.pathname;

  if (next) {
    await next(opts);
  }
  window.location.replace(url);
};

export const Auth0Context = React.createContext<any>(null);

export const useAuth0 = () => useContext(Auth0Context);

interface Props extends Auth0ClientOptions {
  children: any;
  getRedirectUri: () => string;
  onRedirectCallback?: (opts: CallbackData, next: any) => Promise<void>;
  onLoggedIn?: LoggedInCallback;
}

export const Auth0Provider = ({
  children,
  getRedirectUri,
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  onLoggedIn,
  ...initOptions
}: Props) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | undefined>();
  // Keep login and logout available even if the silent session check fails.
  const [auth0Client] = useState(() => new Auth0Client(initOptions));
  const [loading, setLoading] = useState(true);
  const [popupOpen, setPopupOpen] = useState(false);
  const [authError, setAuthError] = useState<string | undefined>(getCallbackError);

  useEffect(() => {
    const initAuth0 = async () => {
      const auth0FromHook = auth0Client;

      if (window.location.search.includes("code=")) {
        const {appState} = await auth0FromHook.handleRedirectCallback();
        const token = await auth0FromHook.getTokenSilently();
        const user = await auth0FromHook.getUser();
        await onRedirectCallback({appState, token, user}, onLoggedIn);
      } else if (!getCallbackError()) {
        await auth0FromHook.checkSession();
      }

      const isAuthenticated = await auth0FromHook.isAuthenticated();

      setIsAuthenticated(isAuthenticated);

      if (isAuthenticated) {
        const user = await auth0FromHook.getUser();
        setUser(user);
      }

      setLoading(false);
    };

    initAuth0().catch(e => {
      console.error(e);
      console.log("Auth0 initialization has failed, the app will be broken.");
      setAuthError(e?.message || String(e));
      setLoading(false);
    });
    // eslint-disable-next-line
  }, []);

  function getAppState() {
    // Must be a local pathname, because router.replace() later expects one.
    return {
      targetUrl: window.location.pathname
    };
  }

  const loginWithPopup = async (p = {}) => {
    const params = {
      appState: getAppState(),
      redirect_uri: getRedirectUri(),
      ...p
    };

    setPopupOpen(true);
    try {
      await auth0Client!.loginWithPopup(params);
    } catch (error) {
      console.error(error);
    } finally {
      setPopupOpen(false);
    }
    const user = await auth0Client!.getUser();
    setUser(user);
    setIsAuthenticated(true);
  };

  const handleRedirectCallback = async () => {
    setLoading(true);
    await auth0Client!.handleRedirectCallback();
    const user = await auth0Client!.getUser();
    setLoading(false);
    setIsAuthenticated(true);
    setUser(user);
  };

  const value = useMemo(() => {
    return {
      isAuthenticated,
      user,
      loading,
      authError,
      popupOpen,
      loginWithPopup,
      handleRedirectCallback,
      getIdTokenClaims: (...p: any[]) => auth0Client!.getIdTokenClaims(...p),
      loginWithRedirect: (p: any[]) => {
        const params = {
          appState: getAppState(),
          redirect_uri: getRedirectUri(),
          ...p
        };
        return auth0Client!.loginWithRedirect(params as any);
      },
      getTokenSilently: (...p: any[]) => auth0Client!.getTokenSilently(...p),
      getTokenWithPopup: (...p: any[]) => auth0Client!.getTokenWithPopup(...p),
      logout: (p: any) => {
        const params = {
          returnTo: getRedirectUri(),
          ...p
        };
        return auth0Client!.logout(params);
      }
    };
  }, [
    isAuthenticated,
    user,
    loading,
    authError,
    popupOpen,
    loginWithPopup,
    handleRedirectCallback,
    auth0Client
  ]);

  return (
    <Auth0Context.Provider value={value}>{children}</Auth0Context.Provider>
  );
};
